import {
  AfterViewInit,
  Component,
  DestroyRef,
  Signal,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EMPTY, Subscription, catchError, of } from 'rxjs';

import { uidMembershipField } from '@core/filters/path-filter/uid-membership';
import {
  EntityInterface,
  FilterableColCfg,
  makeAvailableFilterFields,
  makeFilterCfgMap
} from '@core/models/entities/entity-visualization-interface';
import { CurrentLangService } from '@core/services/common/current-lang.service';
import { PagedEntityService } from '@core/services/entities/paged-entity.service';
import { EntityLabelsTranslationService } from '@core/services/entity-labels-translation/entity-labels-translation.service';
import { ICONS } from '@core/services/icons/icons.service';
import { selectTags } from '@core/store/admin/tags-labels/tags-labels.selectors';
import { selectDataspaceState } from '@core/store/dataspace/dataspace.selectors';
import { selectPlan } from '@core/store/plan/plan.selectors';
import { isNil } from '@core/utils/shared-utils';

import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { DropdownSingletonComponent } from '@shared/components/dropdown-singleton/dropdown-singleton.component';
import { DropdownService } from '@shared/components/dropdown-singleton/dropdown.service';
import { FilterQueryBuilderComponent } from '@shared/components/filter-query-builder/filter-query-builder.component';
import {
  AvailableFilterField,
  FilterConfiguration
} from '@shared/components/filter-query-builder/filter.models';
import {
  ModalComponent,
  ModalConfig,
  ModalSize
} from '@shared/components/modal/modal.component';
import { PaginationConfig } from '@shared/components/pagination/pagination.component';
import { PathFilterComposerComponent } from '@shared/components/path-filter/composer/path-filter-composer.component';
import { PathFilterPanelComponent } from '@shared/components/path-filter/panel/path-filter-panel.component';
import { PathFilterPillsComponent } from '@shared/components/path-filter/pills/path-filter-pills.component';
import { PathFilterSearchBarComponent } from '@shared/components/path-filter/search-bar/path-filter-search-bar.component';
import { PathFilterSortComponent } from '@shared/components/path-filter/sort/path-filter-sort.component';
import { pathOrderBy } from '@shared/components/path-filter/sort/sort-options';
import {
  QualifiersConfig,
  SearchResult
} from '@shared/components/search-query-builder/search-query-builder.component';
import { SearchResultBuilder } from '@shared/components/search-query-builder/search-result-builder';
import { TableComponent } from '@shared/components/table/table.component';
import { SortState, TableConfig } from '@shared/components/table/table.model';
import {
  SearchConfig,
  ToolboxComponent
} from '@shared/components/toolbox/toolbox.component';
import { FocusFirstDirective } from '@shared/directives/focus-first.directive';
import { ReturnFocusDirective } from '@shared/directives/return-focus.directive';
import {
  addOrReplaceConditionInConfig,
  createFilterCondition,
  findFilterField,
  removeConditionFromConfig
} from '@shared/utils/filter-condition.utils';

import { EntityTableService } from '@features/components/entities/entity-table.service';
import { PathFilterBarState } from '@features/components/entities/path-filter-bar/path-filter-bar.state';

import {
  buildPickerFilter,
  keepCandidateRows,
  pathPickerFilter
} from '../deploy-picker-filter.utils';

/** A pickable entity. `label` is null when only the uid is known. */
export interface PickerCandidate {
  uid: string;
  label: string | null;
}

type PickerRow = EntityInterface<unknown>;

/**
 * Picks the entities a configuration should deploy, from the entity grid the rest of the
 * app lists entities with: the same columns, sorting and advanced-search syntax.
 *
 * Restricted to the uids the configuration actually offers — for a messages-kind channel
 * its own messages, for a view-kind one `valid_uids` — so the search narrows within the
 * candidates rather than reaching the whole plan. For GAEL that restriction is the only
 * thing separating a warehouse from any other site of the same entity type.
 */
@Component({
  selector: 'app-deploy-entity-picker',
  providers: [EntityTableService, PagedEntityService, DropdownService, PathFilterBarState],
  imports: [
    TranslatePipe,
    ModalComponent,
    CheckboxComponent,
    ToolboxComponent,
    FilterQueryBuilderComponent,
    TableComponent,
    DropdownSingletonComponent,

    PathFilterSearchBarComponent,
    PathFilterPillsComponent,
    PathFilterSortComponent,
    PathFilterPanelComponent,
    PathFilterComposerComponent,
    FocusFirstDirective,
    ReturnFocusDirective
  ],
  templateUrl: './deploy-entity-picker.component.html',
  styles: ``
})
export class DeployEntityPickerComponent implements AfterViewInit {
  readonly entityType = input.required<string | null>();
  readonly candidates = input.required<PickerCandidate[]>();
  readonly selectedUids = input.required<ReadonlySet<string>>();
  /** GAEL only: there an empty `warehouse_uids` means "export everything", not "export none". */
  readonly clearsFilterWhenEmpty = input<boolean>(false);

  readonly confirmed = output<string[]>();
  readonly cancelled = output<void>();

  protected readonly ICONS = ICONS;
  protected readonly ModalSize = ModalSize;

  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly currentLangService = inject(CurrentLangService);
  private readonly entityTableService = inject(EntityTableService);
  private readonly pagedEntityService = inject(PagedEntityService);
  private readonly entityLabels = inject(EntityLabelsTranslationService);
  /** Scoped to the modal: the shared singleton sits at the modal's own z-index. */
  private readonly localDropdowns = inject(DropdownService);

  private readonly dataspaceState = this.store.selectSignal(selectDataspaceState);
  private readonly plan = this.store.selectSignal(selectPlan);
  private readonly availableTags = this.store.selectSignal(selectTags);

  private readonly localDropdownRef =
    viewChild<DropdownSingletonComponent>('localDropdown');
  private readonly pills = viewChild(PathFilterPillsComponent);

  protected readonly pathBar = inject(PathFilterBarState);
  protected readonly activeGrammar = this.pathBar.activeGrammar;
  /** Defined whenever Path is active: the bar refuses a vocabulary without it. */
  private readonly uidField = computed(() => this.pathBar.fieldAt('uid'));

  protected readonly draft = signal<ReadonlySet<string>>(new Set<string>());
  protected readonly columns = this.entityTableService.columns;
  protected readonly searchQuery = signal<string>('');
  protected readonly rows = signal<PickerRow[]>([]);
  protected readonly failed = signal<boolean>(false);
  protected readonly pagination = signal<PaginationConfig>({
    currentPage: 0,
    totalPages: 1
  });

  private readonly searchResult = signal<SearchResult>(SearchResultBuilder.empty());
  private readonly sortState = signal<SortState<PickerRow>[]>([]);
  /**
   * Edits refetch at once, undebounced: an earlier request answering last would put its rows under
   * the newer filter, so each fetch or page change cancels the one before.
   */
  private rowsFetch: Subscription | null = null;
  protected readonly userFilter = signal<FilterConfiguration | null>(null);
  /**
   * The condition the filter bar should open its popup on, cleared right after: the input
   * is a one-shot instruction, and left set it would reopen the popup on every render.
   */
  protected readonly openFilterForField = signal<string | null>(null);

  /** The fields the filter bar offers, built from the same entity config as the columns. */
  protected readonly availableFilterFields = computed<AvailableFilterField[]>(() => {
    const cfg = this.entityTableService.entityCfg();
    if (isNil(cfg)) return [];
    return makeAvailableFilterFields(cfg, this.entityLabels, this.availableTags());
  });

  /** Ticked rows on the page in view; the draft itself is keyed by uid so it survives paging. */
  protected readonly selectedRows = computed(() => {
    const picked = this.draft();
    return new Set(this.rows().filter((row) => picked.has(row.uid)));
  });

  private readonly filterableColMap = computed<Record<string, FilterableColCfg>>(() => {
    const cfg = this.entityTableService.entityCfg();
    return isNil(cfg) ? {} : makeFilterCfgMap(cfg);
  });

  protected readonly searchConfig: Signal<SearchConfig> = computed(() => ({
    active: true,
    type: 'advanced',
    shortcut: true,
    qualifiers: this.columns().reduce((acc, col) => {
      const filterCfg = this.filterableColMap()[col.key];
      if (!isNil(filterCfg)) {
        acc[this.entityLabels.translateSearchFilterLabel(filterCfg.translationLabel)] = {
          type: 'text',
          description: '',
          supportsRegex: true
        };
      }
      return acc;
    }, {} as QualifiersConfig)
  }));

  protected readonly toolboxSearchConfig = this.pathBar.toolboxSearch(() =>
    this.searchConfig()
  );

  protected readonly totalEntities = computed(
    () => this.pagedEntityService.paginationState().total
  );

  protected readonly tableConfig = computed<TableConfig<PickerRow>>(() => ({
    striped: true,
    bordered: false,
    editable: { inline: false, newTab: false },
    rowNumbers: false,
    columnTooltips: false,
    horizontalScroll: true,
    columnActionSettings: {
      sortable: true,
      filterable: true,
      groupable: false,
      movable: true,
      resizeable: true,
      hideable: true
    },
    selection: {
      enabled: true,
      multiple: true,
      mode: 'checkbox',
      highlightSelected: true,
      action: (row: PickerRow) => this.toggle(row.uid)
    }
  }));

  // `ModalAction.label` is bound straight onto the button, so the key has to be resolved
  // here rather than left for a pipe that never runs.
  protected readonly modalConfig = computed<ModalConfig>(() => {
    this.currentLangService.currentLang();
    return {
      dismissible: true,
      closeOnBackdropClick: true,
      actions: [
        {
          label: this.translate.instant('features.plan.deploy.picker.cancel'),
          callback: () => this.cancelled.emit(),
          color: 'cancel'
        },
        {
          label: this.translate.instant('features.plan.deploy.picker.confirm'),
          callback: () => this.confirm(),
          color: 'primary'
        }
      ]
    };
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => this.rowsFetch?.unsubscribe());
    effect(() =>
      this.entityTableService.setPathFilteredFields(this.pathBar.filteredColumnNames())
    );
    effect(() => {
      this.draft.set(new Set(this.selectedUids()));
    });

    effect(() => {
      const entityType = this.entityType();
      if (isNil(entityType)) return;
      this.entityTableService.setEntityType(entityType);
    });

    effect(() => this.pathBar.entityType.set(this.entityType() ?? ''));
    this.pathBar.acceptsVocabulary.set(
      (vocabulary) => uidMembershipField(vocabulary) !== undefined
    );
    this.pathBar.onEdit(() => this.afterPathEdit());
    this.pathBar.onBarEdit(() => this.afterPathEdit());

    // Re-reads the version name and the data space, which arrive from the store after the
    // modal is already open, so the first fetch waits for them rather than giving up. It also
    // waits until it is known whether the picker opens in Path.
    effect(() => {
      const entityType = this.entityType();
      const versionName = this.entityTableService.entityVersionName();
      const dataSpaceId = this.dataspaceState().dataspace?.id;
      this.candidates();
      if (this.pathBar.fetchHeld()) return;
      if (isNil(entityType) || isNil(versionName) || isNil(dataSpaceId)) return;

      untracked(() => this.fetch());
    });
  }

  ngAfterViewInit(): void {
    const singleton = this.localDropdownRef();
    if (singleton) this.localDropdowns.register(singleton);
  }

  protected onAdvancedQuerySearch(result: SearchResult): void {
    this.searchResult.set(result);
    this.pagedEntityService.resetPagination();
    this.fetch();
  }

  protected onFilterChange(configuration: FilterConfiguration): void {
    this.applyFilter(configuration);
  }

  private applyFilter(configuration: FilterConfiguration | null): void {
    this.userFilter.set(configuration);
    // Marks the columns the filter touches, the same way the entity view does.
    this.entityTableService.setFilterConfiguration(configuration);
    this.pagedEntityService.resetPagination();
    this.fetch();
  }

  protected onSortChange(sortState: SortState<PickerRow>[]): void {
    this.sortState.set(sortState);
    this.fetch();
  }

  protected onPageChange(page: number): void {
    this.rowsFetch?.unsubscribe();
    this.rowsFetch = this.pagedEntityService
      .goToPage<unknown>(page)
      .pipe(catchError(() => of(null)))
      .subscribe((response) => {
        if (isNil(response)) return;
        this.rows.set(this.candidateRows(response.result));
        this.syncPagination();
      });
  }

  /** Adds the column to the filter bar and opens its popup, as the entity view does. */
  protected addFilterOn(column: keyof PickerRow): void {
    const filterCfg = this.filterableColMap()[column as string];
    if (isNil(filterCfg)) return;

    if (this.activeGrammar() === 'path') {
      const field = this.pathBar.fieldForColumn(filterCfg.dbColName);
      if (field) this.pills()?.openField(field);
      return;
    }

    const condition = createFilterCondition(
      findFilterField(this.availableFilterFields(), filterCfg.dbColName)
    );
    if (isNil(condition)) return;

    this.applyFilter(addOrReplaceConditionInConfig(this.userFilter(), condition));

    this.openFilterForField.set(condition.id);
    setTimeout(() => this.openFilterForField.set(null), 100);
  }

  protected removeFilterOn(column: keyof PickerRow): void {
    const filterCfg = this.filterableColMap()[column as string];
    if (isNil(filterCfg)) return;

    if (this.activeGrammar() === 'path') {
      const field = this.pathBar.fieldForColumn(filterCfg.dbColName);
      if (field) this.pathBar.removeConditionsOn(field);
      return;
    }

    this.applyFilter(removeConditionFromConfig(this.userFilter(), filterCfg.dbColName));
  }

  private afterPathEdit(): void {
    this.pagedEntityService.resetPagination();
    this.fetch();
  }

  private fetch(): void {
    if (this.pathBar.fetchHeld()) return;
    const entityType = this.entityType();
    const versionName = this.entityTableService.entityVersionName();
    const dataSpaceId = this.dataspaceState().dataspace?.id;
    if (isNil(entityType) || isNil(versionName) || isNil(dataSpaceId)) return;
    // No candidates cannot be expressed as a restriction, and an unrestricted request
    // would list the whole plan. The modal shows its own copy instead.
    if (this.candidates().length === 0) return;

    const planId = this.plan()?.id;
    const uids = this.candidates().map((candidate) => candidate.uid);
    const path = this.pathBar.fetch();
    const pathFilter = path
      ? pathPickerFilter(this.uidField()!, uids, path.filter)
      : undefined;
    // Without the candidate restriction the request would list the whole plan.
    if (path && isNil(pathFilter)) return;
    const columnOrder = this.entityTableService.getOrderBy(this.sortState());
    this.failed.set(false);

    this.rowsFetch?.unsubscribe();
    this.rowsFetch = this.pagedEntityService
      .fetchEntities<unknown>({
        entityType,
        entityVersionName: versionName,
        dataSpaceId,
        orderBy: path ? pathOrderBy(path.sort, columnOrder) : columnOrder,
        plan: isNil(planId) ? undefined : { id: planId, context: 'visualization' },
        ...(path
          ? {
              path: { filter: pathFilter, search: path.search }
            }
          : {
              searchResult: this.searchResult(),
              filterConfiguration: buildPickerFilter(entityType, uids, this.userFilter())
            })
      })
      .pipe(
        catchError((error: unknown) => {
          // A rejected Path filter is shown in the panel, over the rows of the last success.
          if (path) {
            this.pathBar.reportError(error, this.translate.instant('common.error'));
          } else {
            this.failed.set(true);
            this.rows.set([]);
          }
          return EMPTY;
        })
      )
      .subscribe((response) => {
        this.pathBar.error.set(null);
        this.rows.set(this.candidateRows(response.result));
        this.syncPagination();
      });
  }

  /** @see keepCandidateRows — the restriction is enforced on the way back too, not just asked for. */
  private candidateRows(rows: PickerRow[]): PickerRow[] {
    return keepCandidateRows(
      rows,
      this.candidates().map((candidate) => candidate.uid)
    );
  }

  private syncPagination(): void {
    const state = this.pagedEntityService.paginationState();
    this.pagination.set({ currentPage: state.page, totalPages: state.numPages });
  }

  /** Emits in candidate order so the caller's deploy body keeps the configuration's ordering. */
  protected confirm(): void {
    const picked = this.draft();
    this.confirmed.emit(
      this.candidates()
        .map((candidate) => candidate.uid)
        .filter((uid) => picked.has(uid))
    );
  }

  protected toggle(uid: string): void {
    const next = new Set(this.draft());
    if (next.has(uid)) next.delete(uid);
    else next.add(uid);
    this.draft.set(next);
  }
}
